/**
 * The connectors that represent a phone rather than an account.
 *
 * Calendar and contacts are two connectors, not one, and there is one pair per
 * *install* rather than one pair per account. Both facts are load-bearing:
 *
 * - **Separate**, because they are separate decisions. A user can want the
 *   assistant to know when they're busy without handing over their address
 *   book, and each grants a different iOS permission.
 * - **Per install**, because device sync retires events the device no longer
 *   holds. A single account-wide connector made that retirement account-wide
 *   too, so a second phone's sync cancelled the first phone's events and the
 *   two devices fought forever.
 */

/** Provider ids. Distinct providers rather than one `device` plus a capability,
 *  so every `provider === …` check has to say which half it means. */
export const DEVICE_CALENDAR_PROVIDER = "device_calendar";
export const DEVICE_CONTACTS_PROVIDER = "device_contacts";

/** The provider the pre-split single connector used. Only migrations and the
 *  adoption path in `getOrCreateDeviceConnector` should reference it. */
export const LEGACY_DEVICE_PROVIDER = "device";

export type DeviceConnectorKind = "calendar" | "contacts";

export const deviceConnectorProvider = (kind: DeviceConnectorKind): string =>
  kind === "calendar" ? DEVICE_CALENDAR_PROVIDER : DEVICE_CONTACTS_PROVIDER;

/** True for either half of a device pair, or the single connector they replace. */
export const isDeviceProvider = (provider: string): boolean =>
  provider === DEVICE_CALENDAR_PROVIDER ||
  provider === DEVICE_CONTACTS_PROVIDER ||
  provider === LEGACY_DEVICE_PROVIDER;

/**
 * Whether a connector covers this capability.
 *
 * The legacy single connector counts for both, because clients and servers
 * deploy independently: a client that knows about the split can meet a server
 * that doesn't yet, and asking only for `device_calendar` there finds nothing —
 * so the app decides the phone is disconnected, shows a reconnect banner, and
 * every sync it runs is answered by a `device` row it refuses to recognise.
 * Matching the legacy provider too makes the new client work against either
 * server; the migration then narrows those rows and this stops mattering.
 */
export const connectorCoversDeviceKind = (provider: string, kind: DeviceConnectorKind): boolean =>
  provider === deviceConnectorProvider(kind) || provider === LEGACY_DEVICE_PROVIDER;

/**
 * The ids of every device connector in a list, for excluding their calendars
 * from surfaces that only deal in account calendars.
 *
 * Derived here rather than at each call site because the call sites are the
 * problem: four of them hand-rolled `provider === "device"` against the
 * pre-split name, and the split turned all four into silent no-ops — including
 * one that decides which calendar ids get written back to a preference. Reading
 * the provider through `isDeviceProvider` is what makes the next rename a
 * one-file change.
 *
 * Structurally typed rather than taking `ConnectorSummary`, because the server
 * calls it with drizzle rows and the clients with the API summary; both carry
 * the two fields this reads.
 */
export const deviceConnectorIds = (
  connectors: readonly { id: number; provider: string }[],
): Set<number> => new Set(connectors.filter((c) => isDeviceProvider(c.provider)).map((c) => c.id));

/**
 * Identifies one install's connector within an account.
 *
 * The unique target is `(accountId, provider, providerAccountId)`, so the kind
 * is already carried by `provider` and only the install needs to appear here.
 */
export const deviceProviderAccountId = (installId: string): string => `device-${installId}`;

/** The `providerAccountId` the single pre-split connector was created with. */
export const legacyDeviceProviderAccountId = (accountId: number): string => `device-${accountId}`;

/**
 * What the connector row is called: the phone's own name plus what it
 * contributes — "Shane's iPhone Calendars", "Work iPhone Contacts".
 *
 * Built here rather than on the client so both clients and the admin surface
 * render one format. `connectorRowLabels` has no case for these providers and
 * falls through to `displayName`, which is exactly what should be shown.
 */
export const deviceConnectorDisplayName = (
  deviceName: string | undefined,
  kind: DeviceConnectorKind,
): string => `${deviceName?.trim() || "Device"} ${kind === "calendar" ? "Calendars" : "Contacts"}`;
